import { config } from "@/shared/lib/config";
import { fetcher } from "@/shared/lib/fetcher";
import type { CreateQcRecordBody } from "../../domain/contracts";

export type QcInspectorStatusDTO = {
  isInspector: boolean;
  activeStandardCount: number;
};

export type InspectorStandardRowDTO = {
  id: string;
  name: string;
  description: string | null;
  workStage: string | null;
  workTypeId: string;
  workTypeName: string;
  itemCount: number;
};

export type InspectorStandardItemDTO = {
  id: string;
  title: string;
  description: string | null;
  orderIndex: number;
  isRequired: boolean;
  passCriteria: string | null;
  failCriteria: string | null;
};

export type InspectorStandardDetailDTO = {
  id: string;
  name: string;
  description: string | null;
  workStage: string | null;
  workTypeName: string;
  requirePhoto: boolean;
  requireNotesOnFail: boolean;
  items: InspectorStandardItemDTO[];
};

export type WorkItemTypeDTO = {
  id: string;
  name: string;
  description: string | null;
};

export type MyQcRecordRowDTO = {
  id: string;
  qcStandardId: string;
  standardName: string;
  inspectedAt: string;
  overallStatus: "pass" | "fail" | "pending";
  workItemId: string | null;
};

export type MyQcRecordsListDTO = {
  data: MyQcRecordRowDTO[];
  meta: { total: number; limit: number; offset: number };
};

export type MyQcRecordDetailDTO = {
  record: {
    id: string;
    qcStandardId: string;
    standardName: string;
    inspectedAt: string;
    overallStatus: "pass" | "fail" | "pending";
    workItemId: string | null;
    workItemTypeId: string | null;
    workItemTypeName: string | null;
    notes: string | null;
    location: string | null;
  };
  items: {
    id: string;
    qcStandardItemId: string;
    itemTitle: string;
    itemDescription: string | null;
    orderIndex: number;
    status: "pass" | "fail";
    notes: string | null;
    photoUrl: string | null;
  }[];
};

const baseUrl = `${config.apiUrl}/qc`;

export const qcApi = {
  async getMyInspectorStatus(): Promise<QcInspectorStatusDTO> {
    return fetcher.get<QcInspectorStatusDTO>(`${baseUrl}/me/inspector`);
  },

  async listInspectorStandards(): Promise<InspectorStandardRowDTO[]> {
    return fetcher.get<InspectorStandardRowDTO[]>(
      `${baseUrl}/inspector/standards`,
    );
  },

  async getInspectorStandardDetail(
    standardId: string,
  ): Promise<InspectorStandardDetailDTO> {
    return fetcher.get<InspectorStandardDetailDTO>(
      `${baseUrl}/inspector/standards/${standardId}`,
    );
  },

  async listWorkItemTypes(): Promise<WorkItemTypeDTO[]> {
    return fetcher.get<WorkItemTypeDTO[]>(`${baseUrl}/work-item-types`);
  },

  async listMyRecords(params?: {
    limit?: number;
    offset?: number;
  }): Promise<MyQcRecordsListDTO> {
    const q = new URLSearchParams();
    if (params?.limit != null) q.set("limit", String(params.limit));
    if (params?.offset != null) q.set("offset", String(params.offset));
    const qs = q.toString();
    return fetcher.get<MyQcRecordsListDTO>(
      `${baseUrl}/records/mine${qs ? `?${qs}` : ""}`,
    );
  },

  async getMyRecord(recordId: string): Promise<MyQcRecordDetailDTO> {
    return fetcher.get<MyQcRecordDetailDTO>(`${baseUrl}/records/${recordId}`);
  },

  async createRecord(body: CreateQcRecordBody): Promise<{ recordId: string }> {
    return fetcher.post<{ recordId: string }>(`${baseUrl}/records`, body);
  },
};
